import {
  Ad,
  AdFormat,
  audioSubtypes,
  BatchEventType,
  MediaStreamType,
  Stream,
  Subtitle,
} from '@accedo/one-plugin-sdk';
import { applyDatabinding } from '@dynamic/ui';

import { Item } from '#/client/models';
import {
  Markers,
  PlaybackInfo,
  PlaybackInfoRefs,
  PlaybackInfoResponse,
  PlaybackProgressEventPayload,
  PlayerFeatures,
} from '#/service/features/playback/types';
import { falsyToNull } from '#/service/utils';

import { GeneralContext } from '../useDataContext';

export interface PlaybackDetails {
  advertisements: PlaybackInfo['advertisements'];
  mediaList?: Stream[];
  subtitles: Subtitle[];
  resumePoint?: number;
  markers?: Markers;
  playRefs?: PlaybackInfoRefs;
  playerFeatures: PlayerFeatures;
  progressEventPayload?: PlaybackProgressEventPayload;
}

// Temporary function to get ad config for FreeWheel because instead of having these details in advertisements array
// these are provided in the extra object of each stream for now.
const getAds = (ads?: Ad[], streams?: Stream[]) => {
  const advertisements = ads || [];

  if (!streams?.length) {
    return advertisements;
  }
  const extras = streams[0]?.extras;

  if (extras?.adId) {
    advertisements.push({
      label: 'free-wheel',
      type: AdFormat.FreeWheel,
      videoAssetId: extras.adId,
    });
  }

  return advertisements;
};

// we don't have a proper spec about what image to use as the poster
export const getPoster = (resource: Item) =>
  falsyToNull(resource.images?.still?.wide?.[0]?.url) ??
  resource.images?.poster?.landscape?.[0]?.url;

export const isAudio = (resource: Item | undefined) =>
  !!resource && audioSubtypes.has(resource.subtype);

export const parsePlaybackInfoResponse = (
  res: PlaybackInfoResponse,
  resource: Item | undefined,
  context: GeneralContext
): PlaybackDetails => {
  const playbackInfo = res?.playbackInfo;
  const isAudioItem = isAudio(resource);
  const streams =
    (isAudioItem ? playbackInfo?.audioStreams : playbackInfo?.videoStreams) ??
    [];
  const audioOrVideo = isAudioItem ? resource?.audio : resource?.video;
  const advertisements = getAds(playbackInfo?.advertisements, streams);
  const subtitles = playbackInfo?.subtitles || [];

  // we are choosing one of the incoming `playerFeatures` and `audioPlayerFeatures` based on subtype,
  // so the rest of the app can just use `playerFeatures` without repeating this selection logic.
  const playerFeatures: PlayerFeatures = {
    ...(isAudioItem
      ? playbackInfo?.audioPlayerFeatures
      : playbackInfo?.playerFeatures),
    _debug: isAudioItem ? 'audio' : 'video',
  };

  // core might send `finished`, otherwise fallback to client-side logic
  const seconds = falsyToNull(res?.watched?.progress?.seconds) ?? 0;
  const coreFinished = res?.watched?.progress?.finished || false;
  const duration = Number(audioOrVideo?.duration) || 0;
  const clientFinished = !!(duration && seconds >= duration);
  const finished = coreFinished ?? clientFinished;
  const resumePoint = finished ? 0 : (seconds ?? 0);

  // core might send `markers`, otherwise fallback to client-side logic.
  const markers = res?.markers || {};

  // convert all play refs to relative URLs.
  // typescript needs some convincing that it's still the same type.
  const playRefs = res?.refs;

  const progressEventPayload = res?.events?.[BatchEventType.PlaybackProgress];

  return {
    advertisements,
    subtitles,
    resumePoint,
    markers,
    playRefs,
    mediaList: getMediaList(streams, context),
    playerFeatures,
    progressEventPayload,
  };
};

export const getMediaList = (
  streams: Stream[],
  context: GeneralContext
): Stream[] => {
  const supportedStreamTypes = new Set([
    MediaStreamType.DASH,
    MediaStreamType.HLS,
    MediaStreamType.HTTP,
    MediaStreamType.PROGRESSIVE_HTTP,
  ]);

  // currently, there is no good way for detecting native HLS support.
  // tried to use HTMLMediaElement.canPlayType, but for both Safari and Chrome, it returns:
  // HLS: "maybe" (unhelpful) and DASH: "" (meaning no, which is just wrong).
  // user agent sniffing is bad, but I didn't find any other way of doing this.
  const isNativeHlsPreferred =
    // iOS, any browser
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // desktop Safari
    (navigator.userAgent.includes('Safari') &&
      !navigator.userAgent.includes('Chrome'));

  // browser preference order of stream types. anything not listed here, will be sorted to the bottom.
  const order = isNativeHlsPreferred
    ? [
        MediaStreamType.HLS,
        MediaStreamType.DASH,
        MediaStreamType.HTTP,
        MediaStreamType.PROGRESSIVE_HTTP,
      ]
    : [
        MediaStreamType.DASH,
        MediaStreamType.HLS,
        MediaStreamType.HTTP,
        MediaStreamType.PROGRESSIVE_HTTP,
      ];

  // get a stream type's position in the preference order
  const getPosition = (stream: Stream) => {
    const index = order.indexOf(stream.streamType ?? '');

    return index < 0 ? order.length : index;
  };

  // don't mutate the passed in array. return a new, sorted one.
  const streamsCopy = [...streams];

  // sort by browser preference, ex: start with HLS for Safari
  streamsCopy.sort((streamA, streamZ) => {
    const positionA = getPosition(streamA);
    const positionZ = getPosition(streamZ);

    return positionA - positionZ;
  });

  return (
    streamsCopy
      // remove streams with falsy URL or unsupported stream type
      .filter(
        ({ streamType, url }) =>
          url && streamType && supportedStreamTypes.has(streamType)
      )
      // pick the necessary props, exclude the rest
      .map(({ url, drms, live, extras }) => ({
        // databind all URLs in case there are any liquid expressions that need to be replaced before passing them to the player.
        url: applyDatabinding({ value: url, context }),
        drms,
        live,
        extras,
      }))
  );
};
